import React, {
	useCallback,
	useEffect,
	useMemo,
	type MouseEvent,
	type KeyboardEvent,
	useState,
} from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { SpotlightManager } from '@atlaskit/onboarding';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';
import { FormatRules } from '@atlassian/jira-business-conditional-formatting-sidebar/src/ui/format-rules/index.tsx';
import { SELECTED_ISSUE_PARAM } from '@atlassian/jira-business-constants/src/index.tsx';
import { BusinessDocumentTitle } from '@atlassian/jira-business-entity-common/src/utils/document-title/index.tsx';
// eslint-disable-next-line jira/import/no-restricted-import
import { useQueryBasedViewAutomaticTracking } from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters/src/controllers/default-query-input/index.tsx';
import { useSimpleSearchJQL } from '@atlassian/jira-business-filters/src/controllers/simple-search/index.tsx';
import { useSorting } from '@atlassian/jira-business-filters/src/services/sorting/index.tsx';
import { isOptimisticIssue } from '@atlassian/jira-business-issue-create/src/controllers/issue-create-context/IssueCreateProvider.tsx';
import { PrefetchIssueDataController } from '@atlassian/jira-business-issue-view-critical-data-loader/src/ui/index.tsx';
import { useIssueModalActions } from '@atlassian/jira-business-issue-view/src/common/use-issue-modal-actions/index.tsx';
import ModalIssueApp from '@atlassian/jira-business-issue-view/src/ui/modal-issue-app/index.tsx';
import { SidebarIssueView } from '@atlassian/jira-business-issue-view/src/ui/sidebar-issue-app/index.tsx';
import { RenderStartMark } from '@atlassian/jira-business-performance/src/ui/page-load/index.tsx';
import { BUSINESS_LIST } from '@atlassian/jira-common-constants/src/analytics-sources.tsx';
import { DevDetailsDialogLoaderAsync } from '@atlassian/jira-development-details-loader/src/ui/async.tsx';
import { CoordinationClient } from '@atlassian/jira-engagement/src/ui/coordination-client/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useViewMode } from '@atlassian/jira-issue-context-service/src/main.tsx';
import {
	useAnalyticsEvents,
	AnalyticsEventToProps,
	fireUIAnalytics,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { QuickstartPillVisibilityContainer } from '@atlassian/jira-quickstart-pill-visibility/src/controllers/index.tsx';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { toIssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { useQueryParam } from '@atlassian/react-resource-router';
import { BULK_EDIT_CHANGEBOARDING_MESSAGE_ID, VIEW_EXPERIENCE } from '../../common/constants.tsx';
import { useInteractionMetrics, COLUMN_RESIZED } from '../../common/utils/interaction-metrics.tsx';
import { BulkSelectionContainer } from '../../controllers/bulk-selection/bulk-selection-store/index.tsx';
import { useWatchBulkSelection } from '../../controllers/bulk-selection/watch-bulk-selection/index.tsx';
import { useColumnWidths } from '../../controllers/column-widths/index.tsx';
import { useJWMListFeatures } from '../../controllers/features-context/index.tsx';
import { UpdateFieldsDetail } from '../../controllers/get-issue-fields/index.tsx';
import { useGroupBy } from '../../controllers/group-by/index.tsx';
import GroupUpdater from '../../controllers/group-updater/index.tsx';
import { useHierarchyStore } from '../../controllers/hierarchy/hierarchy-store/index.tsx';
import Hierarchy from '../../controllers/hierarchy/main.tsx';
import { useIssueChangeUpdater } from '../../controllers/issue-change-updater/index.tsx';
import ListActionsBanner from '../../controllers/list-actions-banner/index.tsx';
import ListViewUpdater from '../../controllers/list-updater/index.tsx';
import { useShouldShowReparentModal } from '../../controllers/reparent-modal/index.tsx';
import { useTableAnalytics } from '../../controllers/table-analytics/index.tsx';
import { useClearStaleGroupBy } from '../../controllers/use-clear-stale-group-by/index.tsx';
import { useIssueUpdated } from '../../controllers/use-issue-updated/index.tsx';
import type { ListViewProps } from '../../types.tsx';
import BaseTable from './base-table/index.tsx';
import { useHighlightDoneItems } from './base-table/utils.tsx';
import BulkDeleteModal from './bulk-delete-modal/index.tsx';
import BulkEdit from './bulk-edit/index.tsx';
import { EmptyState } from './empty-state/index.tsx';
import { GroupItem } from './group-item/index.tsx';
import HeaderActions from './header-actions/index.tsx';
import messages from './messages.tsx';
import { InteractionMetrics } from './metrics/index.tsx';
import { AsyncReparentModal } from './reparent-modal/index.tsx';
import { WelcomeModal } from './welcome-modal/index.tsx';
import { useGetColumns } from './utils.tsx';

const FOOTER_HEIGHT = 45;
const ROW_HEIGHT = 40;
const ON_END_REACHED_THRESHOLD = 300;

const STARGATE_URL = '/gateway/api';

// passing in empty componentName so that we can define action name from analytics events
const BaseTableWithAnalytics = AnalyticsEventToProps('', {
	onResize: 'resized',
})((props) => <BaseTable {...props} />);

const ListContent = ({ projectId, projectKey, projectName }: ListViewProps) => {
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();
	const { data: orgId } = useOrgId();

	const interactionMetrics = useInteractionMetrics();
	const [columnWidths, { setColumnWidth }] = useColumnWidths();
	const [
		{
			hierarchyData: { data: issues, loading, error },
		},
	] = useHierarchyStore();
	const viewMode = useViewMode();
	const isViewModeSideBar = viewMode === 'SIDEBAR';
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isFiltering } = useFilteredViewContextInput({ view: 'list' });
	const { isSearching } = useSimpleSearchJQL();
	const [selectedIssueKey, setSelectedIssueKey] = useQueryParam(SELECTED_ISSUE_PARAM);
	const showReparentModal = useShouldShowReparentModal();
	useQueryBasedViewAutomaticTracking(VIEW_EXPERIENCE, {
		loading,
		error,
	});

	const [{ sortBy }] = useSorting();
	const isSorting = sortBy != null;

	const { featureView, isSoftwareFieldsEnabled } = useJWMListFeatures();

	const numIssues = useMemo(
		() => issues.filter((i) => !i.id.match('^SKELETON-ITEM-[0-9]+-id$')).length,
		[issues],
	);

	const [showBulkEditOnboarding, setShowBulkEditOnboarding] = useState(false);
	const [coordinationClient, setCoordinationClient] = useState<CoordinationClient | null>(null);

	if (fg('jira_list_view_bulk_edit_changeboarding')) {
		if (numIssues >= 2 && cloudId && orgId && !coordinationClient) {
			const coordClient = new CoordinationClient(cloudId, STARGATE_URL, false, orgId, 'engagement');
			setCoordinationClient(coordClient);
			coordClient.start(BULK_EDIT_CHANGEBOARDING_MESSAGE_ID).then(setShowBulkEditOnboarding);
		}
	}

	useTableAnalytics();

	const ufoPressTracing = usePressTracing('jwm.list.issue-view-opened');

	const onBeforeOpened = useCallback(
		(event: MouseEvent | KeyboardEvent) => {
			ufoPressTracing(event.timeStamp);
		},
		[ufoPressTracing],
	);

	const filterIssue = useCallback(
		(issue: { id?: string; key: string }) =>
			Boolean(issue.id && !isOptimisticIssue(Number(issue.id)) && issue.key),
		[],
	);

	const { createOpenIssueModalHandler: openIssueModal } = useIssueModalActions({
		onBeforeOpened,
		filterIssue,
	});

	const { toggleHighlightRow, getRowClassName } = useHighlightDoneItems();

	const highlightRow = useCallback(
		(issueKey: string) => toggleHighlightRow(issueKey, true),
		[toggleHighlightRow],
	);

	const { onIssueUpdated } = useIssueUpdated();

	const columns = useGetColumns({
		columnWidths,
		rowHeight: ROW_HEIGHT,
		openIssueModal,
		highlightRow,
		loading,
		projectId,
		onIssueUpdated,
		showBulkEditOnboarding,
	});

	const onResize = (columnKey: string, width: number, analyticsEvent: UIAnalyticsEvent) => {
		interactionMetrics[COLUMN_RESIZED].start();
		setColumnWidth(columnKey, width);
		fireUIAnalytics(analyticsEvent, 'column resized', { columnKey });
	};

	const onModalIssueClose = useCallback(() => {
		setSelectedIssueKey(undefined);
	}, [setSelectedIssueKey]);

	const { onIssueChange } = useIssueChangeUpdater();
	// this needs to be memoised otherwise it rerenders when the table changes causing some elements to blink
	// a common reason for the table to change while this is open is the realtime implementation
	const bentoView = useMemo(
		() =>
			selectedIssueKey != null && (
				<ModalIssueApp
					issueKey={toIssueKey(selectedIssueKey)}
					onClose={onModalIssueClose}
					analyticsSource={BUSINESS_LIST}
					onChange={onIssueChange}
					isSoftwareProject={isSoftwareFieldsEnabled}
				/>
			),
		[selectedIssueKey, onModalIssueClose, onIssueChange, isSoftwareFieldsEnabled],
	);

	const [groupByFieldId] = useGroupBy();

	const isGrouping = groupByFieldId != null;

	useEffect(() => {
		fireTrackAnalytics(createAnalyticsEvent({}), 'list viewed', {
			isGrouping,
			isSorting,
			isFiltering,
			isSearching,
			groupBy: groupByFieldId,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<UFOLoadHold name="list-view" hold={loading} />
			<RenderStartMark view={featureView} loading={loading} />
			<Hierarchy />
			<BaseTableWithAnalytics
				columns={columns}
				onResize={onResize}
				onEndReachedThreshold={ON_END_REACHED_THRESHOLD}
				footerHeight={FOOTER_HEIGHT}
				rowHeight={ROW_HEIGHT}
				getRowClassName={getRowClassName}
				loadingMessage={formatMessage(messages.loadingMoreItems)}
				isSorting={isSorting}
				EmptyState={EmptyState}
				GroupItem={GroupItem}
				projectId={projectId}
				projectKey={projectKey}
				projectName={projectName}
			/>
			<ListViewUpdater />
			<GroupUpdater isBackgroundQueryDisabled={!isGrouping} />
			<UpdateFieldsDetail />
			<UFOSegment name="jira-list-issue-app">
				{isViewModeSideBar ? (
					<SidebarIssueView
						issueKey={selectedIssueKey}
						onClose={onModalIssueClose}
						analyticsSource={BUSINESS_LIST}
						onChange={onIssueChange}
						isSoftwareProject={isSoftwareFieldsEnabled}
					/>
				) : (
					bentoView
				)}
			</UFOSegment>
			<DevDetailsDialogLoaderAsync scopeId="software-list.list" />
			<ListActionsBanner
				showBulkEditOnboarding={showBulkEditOnboarding}
				coordinationClient={coordinationClient}
			/>

			<BulkDeleteModal />
			{showReparentModal && <AsyncReparentModal />}
			<BulkEdit />
			{fg('jira_onboarding_wizard_m1_feature_gate') && <WelcomeModal />}
		</>
	);
};

export const ListView = ({
	projectId,
	projectKey,
	projectName,
	isTeamManaged,
	formattingRulesQuery,
}: ListViewProps) => {
	const { formatMessage } = useIntl();
	const [{ childrenItems: _childrenQueryData }] = useHierarchyStore();
	const pageHeading = formatMessage(messages.listHeading);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClear = useCallback(() => {
		fireTrackAnalytics(createAnalyticsEvent({}), 'groupBy cleared', {
			reason: 'stale',
		});
	}, [createAnalyticsEvent]);

	useClearStaleGroupBy({ onClear });
	useWatchBulkSelection();

	const listViewContent = (
		<>
			<BusinessDocumentTitle pageHeading={pageHeading} />
			<UFOSegment name="jira-list-header-actions">
				<HeaderActions />
			</UFOSegment>
			<SpotlightManager blanketIsTinted={false}>
				<ListContent
					projectKey={projectKey}
					projectName={projectName}
					projectId={projectId}
					isTeamManaged={isTeamManaged}
				/>
			</SpotlightManager>
			<InteractionMetrics />
			<PrefetchIssueDataController />
			{formattingRulesQuery &&
				expValEquals('list_view_conditional_formatting', 'cohort', 'variation') && (
					<FormatRules query={formattingRulesQuery} />
				)}
		</>
	);

	return (
		<BulkSelectionContainer scope="JWMListBulkSelection">
			<QuickstartPillVisibilityContainer scope="ListPageQuickstartPill">
				{listViewContent}
			</QuickstartPillVisibilityContainer>
		</BulkSelectionContainer>
	);
};

export default ListView;
